import { formatAsDateISO } from '~/utils/dates/formatters'
import { CANADA_THANKSGIVING_DATES, US_THANKSGIVING_DATES } from '~/constants/search'
import { ONE_DAY_IN_SECONDS } from '#shared/constants/durations'

import type { Dayjs } from 'dayjs'
import type { DatesInstance } from '~/utils/datetime'
import type { Nullable } from '~/types'

/**
 * Interprets an API date (e.g. date-only "2026-02-24" or ISO string) as that calendar day at 00:00 in a given timezone.
 * Used so calendar grid (in RV timezone) and API date ranges align.
 *
 * @param apiDate API date string (date-only or ISO).
 * @param timezone IANA timezone (e.g. "America/Toronto").
 * @returns Start of that calendar day in the given timezone.
 *
 * @example apiDateToRvDay('2026-02-24', 'America/Toronto') // 2026-02-24 00:00 America/Toronto
 */
export function apiDateToRvDay(apiDate: string, timezone: string): DatesInstance {
  return datetime.tz(datetime.utc(apiDate).format('YYYY-MM-DD'), timezone).startOf('day')
}

/**
 * Returns the number of nights until the given date.
 *
 * @param date The date to calculate the number of nights until.
 * @returns The number of nights until the given date.
 *
 * @example getNumOfNightsUntilDate('2026-02-24') // 0
 */
export function getNumOfNightsUntilDate(date: string) {
  if (!date) return

  return Math.ceil(Math.abs(new Date(date).getTime() - new Date().getTime()) / (1000 * ONE_DAY_IN_SECONDS))
}

/**
 * Returns the default midweek dates for the next week.
 *
 * @returns An object containing the start and end dates in ISO format.
 *
 * @example getDefaultMidweekDates() // { start: '2022-01-03', end: '2022-01-07' }
 */
function getDefaultMidweekDates(): { start: string, end: string } {
  const startPlusOneMonth = datetime()
    .add(1, 'month')
    .add(datetime().add(1, 'month').day() > 1 ? 1 : 0, 'week')
    .day(1)
  const endResult = datetime(startPlusOneMonth).add(4, 'day')

  return {
    start: formatAsDateISO(startPlusOneMonth),
    end: formatAsDateISO(endResult),
  }
}

/**
 * Calculates midweek dates for a given date range.
 *
 * @param start The start date.
 * @param end The end date.
 * @returns An object containing the start and end dates in ISO format.
 *
 * @example getMidweekDates('2022-01-01', '2022-01-07') // { start: '2022-01-03', end: '2022-01-07' }
 */
export function getMidweekDates(start: string | Date, end: string | Date): { start: string, end: string } {
  const startDate = datetime(start)
  const endDate = datetime(end)
  const diff = Math.abs(startDate.diff(endDate, 'day'))

  if (diff >= 7) {
    return getMidweekDatesForBigRange(endDate)
  }

  const isFuture = !isStartOrEndOnWeekend(startDate, endDate)
  const startResult = adjustStartForWeekend(startDate, isFuture)
  const endResult = datetime(startResult).add(4, 'day')

  return {
    start: formatAsDateISO(startResult),
    end: formatAsDateISO(endResult),
  }
}

/**
 * Calculates midweek dates for a large date range.
 *
 * @param endDate The end date.
 * @returns An object containing the start and end dates in ISO format.
 *
 * @example getMidweekDatesForBigRange(datetime('2022-01-15')) // { start: '2022-01-10', end: '2022-01-14' }
 */
function getMidweekDatesForBigRange(endDate: DatesInstance): { start: string, end: string } {
  const start = endDate.day(1)
  const end = datetime(start).add(4, 'day')

  return { start: formatAsDateISO(start), end: formatAsDateISO(end) }
}

/**
 * Checks if the start or end date falls on a weekend.
 *
 * @param startDate The start date.
 * @param endDate The end date.
 * @returns True if the start or end date is on a weekend, false otherwise.
 *
 * @example isStartOrEndOnWeekend(datetime('2022-01-01'), datetime('2022-01-02')) // false
 */
function isStartOrEndOnWeekend(startDate: DatesInstance, endDate: DatesInstance): boolean {
  const sunday = 0
  const saturday = 6
  const weekend = [sunday, saturday]
  const startWeekday = Number(startDate.format('d'))
  const endWeekday = Number(endDate.format('d'))

  return startWeekday === saturday || (!weekend.includes(startWeekday) && weekend.includes(endWeekday))
}

/**
 * Adjusts the start date based on whether it's in the future or past.
 *
 * @param startDate The start date.
 * @param isFuture True if the date is in the future, false otherwise.
 * @returns The adjusted start date.
 *
 * @example adjustStartForWeekend(datetime('2022-01-01'), true) // 2022-01-03
 */
export function adjustStartForWeekend(startDate: DatesInstance, isFuture: boolean): DatesInstance {
  let startResult = datetime(startDate).add(isFuture ? 1 : 0, 'week').day(1)

  if (datetime(startResult).isBefore(datetime())) {
    startResult = datetime().add(1, 'week').day(1)
  }

  return startResult
}

/**
 * Selects midweek dates based on the provided start and end dates.
 * If no dates are provided, it defaults to the next week's Monday to Friday.
 *
 * @param start The start date.
 * @param end The end date.
 * @returns An object containing the start and end dates in ISO format.
 *
 * @example selectMidweekDates('2022-01-01', '2022-01-07') // { start: '2022-01-03', end: '2022-01-07' }
 */
export function selectMidweekDates(start?: string | Date, end?: string | Date): { start: string, end: string } {
  if (!start || !end) {
    return getDefaultMidweekDates()
  }

  return getMidweekDates(start, end)
}

/**
 * Checks if the selected dates are midweek.
 *
 * @param start The start date.
 * @param end The end date.
 * @returns True if the selected dates are midweek, false otherwise.
 *
 * @example isMidweekSelected('2022-01-03', '2022-01-07') // true
 */
export function isMidweekSelected(start?: string | Date, end?: string | Date): boolean {
  if (!start || !end) return false

  const monday = 1
  const startWeekday = Number(datetime(start).format('d'))
  const diff = datetime(end).diff(datetime(start), 'day')

  return startWeekday === monday && diff === 4
}

/**
 * Selects Thanksgiving dates based on the country.
 *
 * @param isUnitedStates True if the country is the United States, false otherwise.
 * @returns An object containing the start and end dates of Thanksgiving.
 *
 * @example selectThanksgivingDates(true) // { start: '2022-11-24', end: '2022-11-25' }
 */
export function selectThanksgivingDates(isUnitedStates: boolean) {
  const start = isUnitedStates ? US_THANKSGIVING_DATES.start : CANADA_THANKSGIVING_DATES.start
  const end = isUnitedStates ? US_THANKSGIVING_DATES.end : CANADA_THANKSGIVING_DATES.end

  return { start, end }
}

/**
 * Checks if the selected dates are Thanksgiving dates.
 *
 * @param isUnitedStates True if the country is the United States, false otherwise.
 * @param start The start date.
 * @param end The end date.
 * @returns True if the selected dates are Thanksgiving dates, false otherwise.
 *
 * @example isThanksgivingSelected(true, '2022-11-24', '2022-11-25') // true
 */
export function isThanksgivingSelected(isUnitedStates: boolean, start: string, end: string): boolean {
  return isUnitedStates
    ? start === US_THANKSGIVING_DATES.start && end === US_THANKSGIVING_DATES.end
    : start === CANADA_THANKSGIVING_DATES.start && end === CANADA_THANKSGIVING_DATES.end
}

/**
 * Returns the number of nights between today and the given date.
 *
 * @param date The date to calculate the number of nights until.
 * @returns The number of nights between today and the given date.
 *
 * @example numberOfNightsUntilDate(new Date(2020, 3, 8)) // 6
 */
export function numberOfNightsUntilDate(date: string | Date) {
  const start = datetime()
  const end = datetime(date)
  const diff = end.diff(start, 'day') - 1
  return diff < 0 ? 0 : diff
}

/**
 * Returns the number of nights between two dates.
 *
 * @param date1 The first date.
 * @param date2 The second date.
 * @returns The number of nights between the two dates.
 *
 * @example numberOfNightsBetweenDates(new Date(2020, 3, 8), new Date(2020, 3, 15)) // 7
 */
export function numberOfNightsBetweenDates(date1: string | Date, date2: string | Date): number {
  if (!date1 || !date2) return 0
  return Math.abs(datetime(date2).diff(datetime(date1), 'days'))
}

/**
 * Returns an array of month options.
 *
 * @returns An array of month options.
 *
 * @example monthOptions() // ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
 */
export function monthOptions(): string[] {
  return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
}

/**
 * Returns an array of day options.
 *
 * @param year The year.
 * @param month The month in format 'MM'.
 * @returns An array of day options.
 *
 * @example dayOptions(2024, '02') // ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29']
 */
export function dayOptions(year?: Nullable<number>, month?: Nullable<string>): string[] {
  const maxDay = year && month ? datetime(`${year}-${month}-01`).daysInMonth() : 31
  return Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, '0'))
}

/**
 * Sorts a date range in ascending order.
 *
 * @param dateRange The date range to sort.
 * @returns The sorted date range.
 *
 * @example sortDateRange([datetime('2020-01-04'), datetime('2020-02-01'), datetime('2020-01-03')]) // [datetime('2020-01-03'), datetime('2020-01-04'), datetime('2020-02-01')]
 */
export function sortDateRange(dateRange: Dayjs[]): Nullable<Dayjs>[] {
  if (!dateRange.length) {
    return [null, null]
  }

  if (dateRange.some((date) => !date?.isValid())) {
    return [null, null]
  }

  return dateRange.sort(function (a, b) {
    return a.unix() - b.unix()
  })
}

/**
 * Looks up consecutive dates, keeping track of DST shifts as well, instead of
 * passing along the original $offset.
 *
 * @param start The start date
 * @param end The end date
 * @param timezone The (RZV) timezone
 * @returns A Dayjs instance
 */
export function eachCalendarDay(start: DatesInstance, end: DatesInstance, timezone: string): DatesInstance[] {
  const dates: DatesInstance[] = []

  let cursor = datetime.utc(start.format('YYYY-MM-DD'))
  const last = datetime.utc(end.format('YYYY-MM-DD'))

  while (cursor.isSameOrBefore(last, 'day')) {
    dates.push(datetime.tz(cursor.format('YYYY-MM-DD'), timezone))
    cursor = cursor.add(1, 'day')
  }

  return dates
}
